import { DateTime } from 'luxon'
import type { ManualTransactionModel } from '@/reducers/admin/allTransactions.slice'
import { TransactionRuleCategoryType } from '@/reducers/admin/transactionRulesReducer'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { addCurrencyArray, formatCurrency } from '@/utils/currencyHelpers'
import { TransactionTypes } from '@/features/transactions/constants'
import { plural } from '../../lib/plural'
import type { FetchError } from '@/reducers/fetch'
import type { WidgetDisplay } from '../../lib/types'

export type ManualTransactionType =
  | TransactionTypes.INCOME
  | TransactionTypes.EXPENSE

/** One row as the member types it: the amount is unsigned, the date as the picker gives it. */
export interface ManualTransactionRow {
  type: ManualTransactionType
  date: string
  description: string
  amount: string
}

export interface DateBounds {
  earliest: DateTime
  latest: DateTime
}

export const EMPTY_ROW: ManualTransactionRow = {
  type: TransactionTypes.EXPENSE,
  date: '',
  description: '',
  amount: '',
}

/** The range the create endpoint accepts: Jan 1 last year to today, after any locked year. */
export const toDateBounds = (
  booksLockedForUserYear: string | null | undefined,
  now: DateTime = DateTime.local()
): DateBounds => {
  const priorYearStart = now.minus({ years: 1 }).startOf('year')
  const afterLock = booksLockedForUserYear
    ? DateTime.local(Number(booksLockedForUserYear) + 1, 1, 1)
    : priorYearStart
  return {
    earliest: afterLock.isValid
      ? DateTime.max(afterLock, priorYearStart)
      : priorYearStart,
    latest: now.endOf('day'),
  }
}

const parseRowDate = (date: string): DateTime =>
  DateTime.fromFormat(date, DATE_FORMATS_LUXON.INPUT)

export const isRowValid = (
  row: ManualTransactionRow,
  bounds: DateBounds
): boolean => {
  const date = parseRowDate(row.date)
  return (
    date.isValid &&
    date >= bounds.earliest &&
    date <= bounds.latest &&
    row.description.trim().length > 0 &&
    Number(row.amount) > 0
  )
}

export const areRowsValid = (
  rows: ManualTransactionRow[],
  bounds: DateBounds
): boolean => rows.length > 0 && rows.every((row) => isRowValid(row, bounds))

/** Every row is a practice transaction, so it is marked business; an expense is saved negative. */
export const toManualTransactions = (
  rows: ManualTransactionRow[]
): ManualTransactionModel[] =>
  rows.map((row) => ({
    date: parseRowDate(row.date).toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY),
    description: row.description.trim(),
    amountInDollars:
      row.type === TransactionTypes.INCOME ? row.amount : `-${row.amount}`,
    type: TransactionRuleCategoryType.business,
  }))

const totalOf = (rows: ManualTransactionRow[]): string =>
  formatCurrency(addCurrencyArray(rows.map((row) => row.amount)))

export const toManualTransactionsOutcome = (
  rows: ManualTransactionRow[]
): { text: string; display: WidgetDisplay } => {
  const income = rows.filter((row) => row.type === TransactionTypes.INCOME)
  const expenses = rows.filter((row) => row.type === TransactionTypes.EXPENSE)
  const count = plural(rows.length, 'transaction')
  const display = { label: `${count} added`, meta: totalOf(rows) }

  if (expenses.length === 0) {
    return {
      text: `The member saved ${count} totaling ${totalOf(income)}, all as practice income.`,
      display,
    }
  }
  if (income.length === 0) {
    return {
      text: `The member saved ${count} totaling ${totalOf(expenses)}, all as practice expenses.`,
      display,
    }
  }
  return {
    text: `The member saved ${count}: ${income.length} income totaling ${totalOf(income)} and ${plural(expenses.length, 'expense')} totaling ${totalOf(expenses)}, all as practice transactions.`,
    display: {
      ...display,
      meta: `${totalOf(income)} in · ${totalOf(expenses)} out`,
    },
  }
}

const SAVE_FAILED = "Couldn't save that. Try again."

/** A refusal (400) carries the server's reason, such as a date it won't accept; anything else is retryable. */
export const toSaveErrorMessage = (error: FetchError | undefined): string =>
  error?.statusCode === 400 && error.message ? error.message : SAVE_FAILED
